import type { JSX } from 'react';

import log from 'loglevel';

import LocalAuthProvider, { useLocalAuth } from '../LocalAuthProvider/LocalAuthProvider';

// =============================================================================
// Types
// =============================================================================

/**
 * Unified user profile
 */
export interface UnifiedUser {
  sub: string;
  email?: string;
  email_verified?: boolean;
  name?: string;
  given_name?: string;
  family_name?: string;
  nickname?: string;
  picture?: string;
  locale?: string;
  roles?: string[];
  tenantId?: string;
}

/**
 * Unified authentication context
 */
export interface UnifiedAuthContext {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: UnifiedUser | null;
  error: Error | null;
  getAccessTokenSilently: () => Promise<string>;
  logout: (options?: { returnTo?: string }) => Promise<void>;
  login: (options?: { redirectUri?: string }) => Promise<void>;
  provider: 'local';
}

// =============================================================================
// Unified Hook Implementation
// =============================================================================

/**
 * Hook that provides unified auth interface for local auth (BetterAuth)
 */
function useLocalAuthUnified(): UnifiedAuthContext {
  const { isAuthenticated, isLoading, user, error, getAccessTokenSilently, logout } =
    useLocalAuth();

  return {
    isAuthenticated,
    isLoading,
    user: user
      ? {
          sub: user.sub,
          email: user.email,
          email_verified: user.email_verified,
          name: user.name,
          given_name: user.given_name,
          family_name: user.family_name,
          nickname: user.preferred_username,
          picture: user.picture,
          locale: user.locale,
          roles: user.roles,
          tenantId: user.tenantId,
        }
      : null,
    error,
    getAccessTokenSilently,
    logout: async () => {
      await logout({ returnTo: '/login' });
    },
    login: async () => {
      // Local auth uses /login page directly; this is a no-op
    },
    provider: 'local',
  };
}

// =============================================================================
// Context Consumer Components
// =============================================================================

interface AuthConsumerProps {
  children: (auth: UnifiedAuthContext) => JSX.Element;
}

function LocalConsumer({ children }: AuthConsumerProps): JSX.Element {
  const auth = useLocalAuthUnified();
  return children(auth);
}

// =============================================================================
// Main Provider Component
// =============================================================================

interface UnifiedAuthProviderProps {
  children: React.ReactNode;
}

/**
 * UnifiedAuthProvider wraps the application with the local auth provider.
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <UnifiedAuthProvider>
 *       <YourAppContent />
 *     </UnifiedAuthProvider>
 *   );
 * }
 * ```
 */
export default function UnifiedAuthProvider({ children }: UnifiedAuthProviderProps): JSX.Element {
  log.info(`Using authentication provider: local`);

  return <LocalAuthProvider>{children}</LocalAuthProvider>;
}

// =============================================================================
// Unified Hook Export
// =============================================================================

/**
 * useUnifiedAuth hook provides a consistent interface for local auth.
 *
 * This hook must be used within the UnifiedAuthProvider context.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isAuthenticated, user, logout, getAccessTokenSilently, provider } = useUnifiedAuth();
 *
 *   if (!isAuthenticated) {
 *     return <p>Please log in</p>;
 *   }
 *
 *   return (
 *     <div>
 *       <p>Welcome, {user?.name}!</p>
 *       <p>Using provider: {provider}</p>
 *       <button onClick={() => logout()}>Logout</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useUnifiedAuth(): UnifiedAuthContext {
  return useLocalAuthUnified();
}

// =============================================================================
// Named Exports
// =============================================================================

export { LocalConsumer, UnifiedAuthProvider, useLocalAuthUnified };
